import {downloadFile, nullify, ResponseData, showDialog} from '@dvrd/dvr-controls';
import IDate from '@dvrd/idate';
import React, {useCallback, useContext, useEffect, useRef, useState} from 'react';
import {ISO_DATE_FORMAT} from '../../util/constants';
import {PeriodLabel, SalesCategory, SalesColumn, SalesProductView} from '../../util/enums';
import {ActivateHandle, OrderDirection} from '../../util/interfaces';
import {exportSalesDashboardV2, getSalesDashboardStatsV2, getSalesDashboardV2Products} from '../../util/requestSender';
import {GeneralSalesProduct, SalesDashboardFilterGroup, SalesDashboardStats} from '../../util/types';
import {createErrorMessage, deepClone} from '../../util/utils';
import {onSetCrumbs} from '../../util/events';
import {AppContext} from '../misc/appContext';
import SalesProductDetailsController from './productDetails/salesProductDetailsController';
import SalesDashboard from './salesDashboard';
import {useTranslation} from 'react-i18next';
import {PRESET_PERIODS} from '../../util/dateUtil.ts';
import {allSalesDashboardColumns, salesDashboardColumnMap} from '../../util/salesDashboardUtil.tsx';

type LoadParams = {
    countryCodes?: string;
    accountIDs?: Set<string>;
    period?: PeriodLabel;
    customPeriodStart?: IDate | null;
    customPeriodEnd?: IDate | null;
    order?: SalesColumn;
    orderDirection?: OrderDirection;
    page?: number;
    limit?: number;
    filters?: Array<SalesDashboardFilterGroup>;
    showHiddenProducts?: boolean;
    search?: string;
};

export function getDatesFromPeriod(
    period: PeriodLabel,
    customStart: IDate | null,
    customEnd: IDate | null
): [IDate | null, IDate | null] {
    switch (period) {
        case PeriodLabel.CUSTOM:
            return [customStart, customEnd];
        case PeriodLabel.ALL:
            // The backend always expects a startDate and endDate and does not load on null values.
            // We go back far enough (2020) to know for sure we get all data
            return [new IDate('2020-01-01'), IDate.now()];
        default: {
            const getRange = PRESET_PERIODS[period];
            return getRange?.() ?? [new IDate('2020-01-01'), IDate.now()];
        }
    }
}

function sanitizeFilters(filters: Array<SalesDashboardFilterGroup>): Array<SalesDashboardFilterGroup> {
    // Backwards loop to allow deleting items immediately
    for (let ix = filters.length - 1; ix >= 0; ix--) {
        const filterGroup = filters[ix];
        filterGroup.filters = filterGroup.filters.filter(filter => filter.valueSet);
        if (!filterGroup.filters.length) filters.splice(ix, 1);
    }
    return filters;
}

const initialFilters: Array<SalesDashboardFilterGroup> = [
    {field: SalesColumn.QUANTITY, filters: [], group_type: 'AND'},
    {field: SalesColumn.REVENUE, filters: [], group_type: 'AND'},
    {field: SalesColumn.ORGANIC_SALES, filters: [], group_type: 'AND'},
    {field: SalesColumn.RETURN_QUANTITY, filters: [], group_type: 'AND'},
    {field: SalesColumn.RETURN_REVENUE, filters: [], group_type: 'AND'},
    {field: SalesColumn.RETURN_COSTS, filters: [], group_type: 'AND'},
    {field: SalesColumn.RETURN_COMMISSION, filters: [], group_type: 'AND'},
    {field: SalesColumn.AD_SPENT, filters: [], group_type: 'AND'},
    {field: SalesColumn.SPONSORED_SALES, filters: [], group_type: 'AND'},
    {field: SalesColumn.ACOS, filters: [], group_type: 'AND'},
    {field: SalesColumn.CPC, filters: [], group_type: 'AND'},
    {field: SalesColumn.PROD_COSTS, filters: [], group_type: 'AND'},
    {field: SalesColumn.COMMISSION, filters: [], group_type: 'AND'},
    {field: SalesColumn.VAT, filters: [], group_type: 'AND'},
    {field: SalesColumn.PROFIT, filters: [], group_type: 'AND'},
    {field: SalesColumn.MARGIN, filters: [], group_type: 'AND'},
    {field: SalesColumn.STOCK, filters: [], group_type: 'AND'}
];

const initialVisibleColumns = new Set(allSalesDashboardColumns);

export default function SalesDashboardController() {
    const {account} = useContext(AppContext).accountContext;
    const {t} = useTranslation();
    const [search, setSearch] = useState('');
    const [countryCodes, setCountryCodes] = useState<string>('NL,BE');
    const [accountIDs, setAccountIDs] = useState<Set<string>>(new Set([account!.id]));
    const [period, setPeriod] = useState<PeriodLabel>(PeriodLabel.LAST_30_DAYS);
    const [customPeriodStart, setCustomPeriodStart] = useState<IDate | null>(null);
    const [customPeriodEnd, setCustomPeriodEnd] = useState<IDate | null>(null);
    const [page, setPage] = useState(0);
    const [limit, setLimit] = useState(50);
    const [total, setTotal] = useState(0);
    const [order, setOrder] = useState<SalesColumn>(SalesColumn.QUANTITY);
    const [orderDirection, setOrderDirection] = useState<OrderDirection>(OrderDirection.DESC);
    const [filters, setFilters] = useState<Array<SalesDashboardFilterGroup>>(initialFilters.slice());
    const [stats, setStats] = useState<SalesDashboardStats | null>(null);
    const [comparingStats, setComparingStats] = useState<SalesDashboardStats | null>(null);
    const [products, setProducts] = useState<Array<GeneralSalesProduct>>([]);
    const [loadingStats, setLoadingStats] = useState(true);
    const [loadingProducts, setLoadingProducts] = useState(true);

    const [visibleColumns, setVisibleColumns] = useState<Set<SalesColumn>>(initialVisibleColumns);
    const [comparisonVisible, setComparisonVisible] = useState(true);

    const productDetailsRef = useRef<ActivateHandle>(null);
    const searchDebounce = useRef<number | undefined>(undefined);

    function onChangeSearch(value: string) {
        clearSearchDebounce();
        setSearch(value);
        if (!value) {
            setPage(0);
            void loadProducts({search: '', page: 0});
        } else {
            searchDebounce.current = window.setTimeout(() => {
                setPage(0);
                void loadProducts({search: value, page: 0});
            }, 500);
        }
    }

    function onEnterSearch() {
        clearSearchDebounce();
        void loadProducts();
    }

    const clearSearchDebounce = useCallback(() => {
        window.clearTimeout(searchDebounce.current);
        searchDebounce.current = undefined;
    }, []);

    function onChangeCategoryVisibility(category: SalesCategory) {
        return function (toggled: boolean) {
            if (category === SalesCategory.COMPARISONS) return setComparisonVisible(toggled);

            const columns = salesDashboardColumnMap[category];
            const visible = new Set(visibleColumns);
            for (const column of columns) {
                if (toggled) visible.add(column);
                else visible.delete(column);
            }
            setVisibleColumns(visible);
        };
    }

    function onChangeColumnVisibility(
        _column: SalesColumn | null,
        _selected: boolean,
        visibleColumns: Array<SalesColumn>
    ) {
        setVisibleColumns(new Set(visibleColumns));
    }

    function onExport(exportType: 'excel' | 'csv') {
        return function () {
            setLoadingProducts(true);
            const [startDate, endDate] = getDatesFromPeriod(period, customPeriodStart, customPeriodEnd);
            exportSalesDashboardV2({
                data: {
                    order,
                    account_ids: Array.from(accountIDs),
                    country_codes: nullify(Array.from(countryCodes.split(','))),
                    end_date: endDate!.format(ISO_DATE_FORMAT),
                    start_date: startDate!.format(ISO_DATE_FORMAT),
                    search: nullify(search),
                    filters: filters,
                    order_direction: orderDirection,
                    export_type: exportType
                },
                callback: (data: ResponseData, response: Response) => {
                    if (data.success) {
                        response.blob().then((blob: Blob) => {
                            const header = response.headers.get('Content-Disposition');
                            let suffix: string = '';
                            if (exportType === 'excel') suffix = '.xlsx';
                            else if (exportType === 'csv') suffix = '.csv';
                            let fileName = `sales-dashboard${suffix}`;
                            if (header)
                                fileName = header.substring(
                                    header.indexOf('filename=') + 'filename='.length,
                                    header.length
                                );
                            downloadFile(blob, fileName);
                            setLoadingProducts(false);
                        });
                    } else {
                        setLoadingProducts(false);
                        showDialog(
                            createErrorMessage(data.message ?? t('exportError', 'Fout bij het exporteren naar Excel.'))
                        );
                    }
                }
            });
        };
    }

    function onChangeCountries(countryCodes: string) {
        setCountryCodes(countryCodes);
        setPage(0);
        void loadAll({countryCodes: countryCodes, page: 0});
    }

    function onChangeAccounts(_: string | null, _selected: boolean, accountIDs: Array<string>) {
        const ids = new Set(accountIDs);
        setAccountIDs(ids);
        setPage(0);
        void loadAll({accountIDs: ids, page: 0});
    }

    function onChangePeriod(range: [IDate | null, IDate | null], period: PeriodLabel | null) {
        if (!period) period = PeriodLabel.CUSTOM;
        const [startDate, endDate] = range;
        setPeriod(period);
        if (period === PeriodLabel.CUSTOM) {
            setCustomPeriodStart(startDate);
            setCustomPeriodEnd(endDate);
        }
        setPage(0);
        void loadAll({period, page: 0, customPeriodStart: startDate, customPeriodEnd: endDate});
    }

    function onChangeOrder(_order: SalesColumn) {
        return function () {
            let direction: OrderDirection;
            if (_order === order) {
                if (orderDirection === OrderDirection.ASC) direction = OrderDirection.DESC;
                else direction = OrderDirection.ASC;
            } else {
                direction = _order === SalesColumn.TITLE ? OrderDirection.ASC : OrderDirection.DESC;
                setOrder(_order);
            }
            setOrderDirection(direction);
            setPage(0);
            void loadProducts({order: _order, orderDirection: direction, page: 0});
        };
    }

    function onClearFilters() {
        setFilters(initialFilters.slice());
    }

    function onApplyFilter(filterGroup: SalesDashboardFilterGroup) {
        const _filters = deepClone(filters);
        let found: boolean = false;
        for (let ix = 0; ix < _filters.length; ix++) {
            if (_filters[ix].field !== filterGroup.field) continue;
            _filters.splice(ix, 1, filterGroup);
            found = true;
            break;
        }
        if (!found) _filters.push(filterGroup);
        setFilters(_filters);
        setPage(0);
        void loadProducts({page: 0, filters: _filters});
    }

    function onChangePage(nextPage: number) {
        return function () {
            setPage(nextPage);
            void loadProducts({page: nextPage});
        };
    }

    function onChangeLimit(limit: number) {
        setLimit(limit);
        setPage(0);
        void loadProducts({limit, page: 0});
    }

    function onClickProduct(product: GeneralSalesProduct | null, view?: SalesProductView) {
        return function () {
            productDetailsRef.current?.activate({
                product,
                accountIDs,
                period,
                customStart: customPeriodStart,
                customEnd: customPeriodEnd,
                view
            });
        };
    }

    async function loadAll(params?: LoadParams) {
        await Promise.all([loadStats(params), loadProducts(params)]);
    }

    function loadStats(params?: LoadParams) {
        return new Promise<void>(resolve => {
            const _accountIDs = params?.accountIDs ?? accountIDs;
            const _period = params?.period ?? period;
            const _customStart = params?.customPeriodStart ?? customPeriodStart;
            const _customEnd = params?.customPeriodEnd ?? customPeriodEnd;
            const [startDate, endDate] = getDatesFromPeriod(_period, _customStart, _customEnd);
            const _countryCodes = (params?.countryCodes ?? countryCodes).split(',');
            if (!startDate || !endDate || !_accountIDs.size) return resolve();
            setLoadingStats(true);
            getSalesDashboardStatsV2({
                data: {
                    account_ids: Array.from(_accountIDs),
                    country_codes: _countryCodes,
                    end_date: endDate.format(ISO_DATE_FORMAT),
                    start_date: startDate.format(ISO_DATE_FORMAT)
                },
                callback: (data: ResponseData) => {
                    setLoadingStats(false);
                    if (data.success) {
                        setStats(data.stats);
                        setComparingStats(data.comparing_stats);
                    } else
                        showDialog(
                            createErrorMessage(
                                data.message ??
                                    t('salesDashboardV2.errorLoadTotals', 'Het laden van de totalen is niet gelukt.')
                            ),
                            t('salesDashboardV2.loadFailed', 'Laden mislukt')
                        );
                    resolve();
                }
            });
        });
    }

    function loadProducts(params?: LoadParams) {
        return new Promise<void>(resolve => {
            clearSearchDebounce();
            const _accountIDs = params?.accountIDs ?? accountIDs;
            const _period = params?.period ?? period;
            const _customStart = params?.customPeriodStart ?? customPeriodStart;
            const _customEnd = params?.customPeriodEnd ?? customPeriodEnd;
            const [startDate, endDate] = getDatesFromPeriod(_period, _customStart, _customEnd);
            if (!startDate || !endDate || !_accountIDs.size) return resolve();
            setLoadingProducts(true);
            const _order = params?.order ?? order;
            const _orderDirection = params?.orderDirection ?? orderDirection;
            const _countryCodes = (params?.countryCodes ?? countryCodes).split(',');
            const _page = params?.page ?? page;
            const _limit = params?.limit ?? limit;
            const _filters = sanitizeFilters(deepClone(params?.filters ?? filters));
            const _search = params?.search ?? search;
            getSalesDashboardV2Products({
                data: {
                    page: _page,
                    order: _order,
                    limit: _limit,
                    account_ids: Array.from(_accountIDs),
                    country_codes: nullify(Array.from(_countryCodes)),
                    end_date: endDate!.format(ISO_DATE_FORMAT),
                    start_date: startDate!.format(ISO_DATE_FORMAT),
                    search: nullify(_search),
                    filters: _filters,
                    order_direction: _orderDirection
                },
                callback: (data: ResponseData) => {
                    setLoadingProducts(false);
                    if (data.success) {
                        setProducts(data.sales_products);
                        if (data.total > -1) setTotal(data.total);
                    } else
                        showDialog(
                            createErrorMessage(
                                data.message ??
                                    t(
                                        'salesDashboardV2.errorLoadProducts',
                                        'Het laden van de producten is niet gelukt.'
                                    )
                            ),
                            t('salesDashboardV2.loadFailed', 'Laden mislukt')
                        );
                    resolve();
                }
            });
        });
    }

    useEffect(() => {
        onSetCrumbs([{label: t('salesDashboardV2.breadcrumb', 'Sales Dashboard')}]);
        void loadAll();
    }, []);

    return (
        <>
            <SalesDashboard
                onChangeSearch={onChangeSearch}
                onEnterSearch={onEnterSearch}
                onChangeCategoryVisibility={onChangeCategoryVisibility}
                onChangeColumnVisibility={onChangeColumnVisibility}
                onExport={onExport}
                onChangeCountries={onChangeCountries}
                onChangeAccounts={onChangeAccounts}
                onChangePeriod={onChangePeriod}
                onChangeOrder={onChangeOrder}
                onClearFilters={onClearFilters}
                onApplyFilter={onApplyFilter}
                onChangePage={onChangePage}
                onChangeLimit={onChangeLimit}
                onClickProduct={onClickProduct}
                search={search}
                countryCodes={countryCodes}
                accountIDs={accountIDs}
                period={period}
                customPeriodStart={customPeriodStart}
                customPeriodEnd={customPeriodEnd}
                page={page}
                limit={limit}
                total={total}
                order={order}
                filters={filters}
                products={products}
                loadingProducts={loadingProducts}
                stats={stats}
                comparingStats={comparingStats}
                loadingStats={loadingStats}
                visibleColumns={visibleColumns}
                comparisonVisible={comparisonVisible}
            />
            <SalesProductDetailsController
                ref={productDetailsRef}
                onReload={loadProducts}
                countryCodes={countryCodes}
            />
        </>
    );
}
